import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Rings } from "react-loader-spinner";
import { sassFalse } from "sass";
import secureLocalStorage from "react-secure-storage";

const VoiceAssistant = () => {
  const { t } = useTranslation();
  const [ws, setWs] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [audioQueue, setAudioQueue] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [messages, setMessages] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const recordButtonRef = useRef(null);
  const audioContextRef = useRef(null);
  const gainNodeRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const [isThinking, setIsThinking] = useState(false);

  const loginData = secureLocalStorage.getItem("loginCredentials");

  useEffect(() => {
    if (audioQueue.length > 0 && !isPlaying) {
      playNextInQueue();
    }
  }, [audioQueue]);

  const appendMessage = (msg) => {
    setMessages((prevMessages) => [...prevMessages, msg]);
  };

  const connectWebSocket = () => {
    const userId = loginData.user.id;
    const socket = new WebSocket(
      "wss://apollo-python-backend.octalooptechnologies.com/ws/ws"
    );

    socket.onopen = () => {
      socket.send(JSON.stringify({ type: "auth", userId: userId }));

      appendMessage("Connected to Donna AI server.");
    };
    socket.onclose = () => appendMessage("Disconnected from Donna AI server.");
    socket.onerror = (error) =>
      appendMessage("WebSocket error: " + error.message);

    socket.onmessage = async (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === "response.audio.chunk") {
          appendMessage("[Received audio chunk]");
          setIsThinking(false);

          const byteString = atob(data.audio_chunk_base64);
          const byteArray = new Uint8Array(byteString.length);
          for (let i = 0; i < byteString.length; i++) {
            byteArray[i] = byteString.charCodeAt(i);
          }

          const wavFile = createWavFile(byteArray);

          if (!audioContextRef.current) {
            audioContextRef.current = new (window.AudioContext ||
              window.webkitAudioContext)();
            gainNodeRef.current = audioContextRef.current.createGain();
            gainNodeRef.current.gain.value = 2.0; // Amplify the audio volume
            gainNodeRef.current.connect(audioContextRef.current.destination);
          }

          const audioBuffer = await audioContextRef.current.decodeAudioData(
            wavFile
          );
          setAudioQueue((prevQueue) => [...prevQueue, audioBuffer]);
        } else if (data.type === "response.text.delta") {
          appendMessage(`Donna AI (partial): ${data.delta}`);
        } else if (data.type === "response.text") {
          appendMessage(`Donna AI: ${data.text}`);
        } else {
          appendMessage(`Received: ${JSON.stringify(data)}`);
        }
      } catch (err) {
        appendMessage("Received non-JSON message: " + event.data);
      }
    };

    setWs(socket);
  };

  useEffect(() => {
    connectWebSocket();
    return () => {
      disconnectWebSocket();
    };
  }, []);

  const disconnectWebSocket = () => {
    if (ws) {
      ws.close();
      setWs(null);
      appendMessage("Disconnected from server.");
    }
  };

  const toggleRecording = async () => {
    if (!navigator.mediaDevices || !window.MediaRecorder) {
      appendMessage("MediaRecorder API not supported in this browser.");
      return;
    }

    if (isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsProcessing(false);
    } else {
      try {
        setIsProcessing(true);
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        mediaRecorderRef.current = new MediaRecorder(stream);
        audioChunksRef.current = [];

        mediaRecorderRef.current.ondataavailable = (e) => {
          audioChunksRef.current.push(e.data);
        };

        mediaRecorderRef.current.onstop = () => {
          const blob = new Blob(audioChunksRef.current, {
            type: "audio/webm; codecs=opus",
          });
          const reader = new FileReader();

          reader.onloadend = () => {
            const arrayBuffer = reader.result;
            const byteArray = new Uint8Array(arrayBuffer);
            const b64Audio = arrayBufferToBase64(byteArray);
            sendAudio(b64Audio);
            setIsProcessing(false);
          };

          reader.readAsArrayBuffer(blob);
        };

        mediaRecorderRef.current.start();
        setIsRecording(true);
      } catch (error) {
        appendMessage("Error accessing microphone: " + error.message);
        setIsProcessing(false);
      }
    }
  };

  const sendAudio = (b64Audio) => {
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      appendMessage("WebSocket not connected. Please click 'Connect' first.");
      return;
    }

    const message = {
      type: "audio",
      content: b64Audio,
    };

    ws.send(JSON.stringify(message));
    setIsThinking(true);
    appendMessage("[Sent audio to server]");
  };

  const reconnectWebSocket = () => {
    disconnectWebSocket();
    connectWebSocket();
  };

  function stopAllActions() {
    console.log("Stopping all actions");
    if (!isProcessing) {
      console.log("Is Processing...");
      reconnectWebSocket();
    }
    setIsProcessing(false);
    setIsThinking(false);
    setIsRecording(false);
    setIsPlaying(false);

    setAudioQueue([]);

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "inactive"
    ) {
      mediaRecorderRef.current.stop();
    }
  }

  const arrayBufferToBase64 = (buffer) => {
    let binary = "";
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  };

  const createWavFile = (pcmData, sampleRate = 24000) => {
    const numChannels = 1;
    const bitsPerSample = 16;
    const bytesPerSample = bitsPerSample / 8;
    const blockAlign = numChannels * bytesPerSample;
    const byteRate = sampleRate * blockAlign;

    const wavHeader = new ArrayBuffer(44);
    const view = new DataView(wavHeader);

    const writeString = (view, offset, string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };

    writeString(view, 0, "RIFF");
    view.setUint32(4, 36 + pcmData.byteLength, true);
    writeString(view, 8, "WAVE");
    writeString(view, 12, "fmt ");
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, byteRate, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitsPerSample, true);
    writeString(view, 36, "data");
    view.setUint32(40, pcmData.byteLength, true);

    const wavBuffer = new Uint8Array(wavHeader.byteLength + pcmData.byteLength);
    wavBuffer.set(new Uint8Array(wavHeader), 0);
    wavBuffer.set(new Uint8Array(pcmData), wavHeader.byteLength);

    return wavBuffer.buffer;
  };

  const playNextInQueue = () => {
    if (audioQueue.length > 0) {
      const buffer = audioQueue[0];
      setIsPlaying(true);

      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext ||
          window.webkitAudioContext)();
        gainNodeRef.current = audioContextRef.current.createGain();
        gainNodeRef.current.gain.value = 9.0; // Amplify the audio volume
        gainNodeRef.current.connect(audioContextRef.current.destination);
      }

      const source = audioContextRef.current.createBufferSource();
      source.buffer = buffer;
      source.connect(gainNodeRef.current);
      source.onended = () => {
        setIsPlaying(false);
        setAudioQueue((prevQueue) => prevQueue.slice(1));
      };
      source.start(0);
    }
  };

  return (
    <div className="drop-shadow-md mt-[50px] w-full text-center flex flex-col justify-center items-center bg-[#F8F8F8] px-[5px] py-[19px] border-r-2 border-r-[#fff] border-solid rounded-[28px]">
      <h4 className="medium font-semibold text-blue mt-[120px] max-exl:mt-[0px] sm-s:mt-[60px]">
        {t("dashboard.voiceAssistant.title")}
        <span className="light-blue-color font-montserrat">
          {" "}
          {t("dashboard.voiceAssistant.name")}
        </span>
      </h4>
      <img
        src="../../assets/images/dashboard/groupAI.png"
        alt=""
        className="mb-[20px] mt-[17px]"
      />
      <p className="large max-w-[400px] w-full text-blue my-[20px]">
        {t("dashboard.voiceAssistant.message")}
      </p>
      <div className="h-[24px] flex items-center flex-col justify-center mb-4">
        <span
          className={`text-blue animate-pulse ${!isPlaying ? "invisible" : ""}`}
        >
          Speaking...
        </span>
        {isProcessing ? (
          <span
            className={`text-blue animate-pulse ${
              !isProcessing ? "invisible" : ""
            }`}
          >
            Listening...
          </span>
        ) : (
          <span
            className={`text-blue animate-pulse ${
              !isThinking ? "invisible" : ""
            }`}
          >
            Thinking...
          </span>
        )}
      </div>

      <div className="flex gap-[50px] sm-s:gap-[20px] items-center justify-center">
        <button>
          <div
            className={`circle voice-circle ${isRecording ? "active" : ""}`}
            ref={recordButtonRef}
            onClick={toggleRecording}
          >
            {isProcessing ? (
              <Rings
                height="40"
                width="40"
                color="white"
                radius="7"
                ariaLabel="loading-indicator"
              />
            ) : (
              <Icon
                icon="streamline:voice-mail"
                width={36}
                height={36}
                className="cursor-pointer voice-icon"
              />
            )}
          </div>
        </button>
        <button className="rounded-full" onClick={stopAllActions}>
          <img
            src="../../assets/images/dashboard/cross.png"
            alt="Close"
            className="drop-shadow-md cursor-pointer"
          />
        </button>
      </div>
    </div>
  );
};

export default VoiceAssistant;
